import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import {CREDIT_WALLET_DATA, USER_DATA} from 'user/user-prop-types';
import {PROMO_CODE_DATA} from 'checkout/checkout-prop-types';
import {connect} from 'react-redux';
import {v4 as uuidV4} from 'uuid';
import Alert from '@spothero/ui/alert';
import Form from '@spothero/ui/form';
import FormGroupError from '@spothero/ui/form-group-error';
import TextButton from '@spothero/ui/text-button';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import SHLoader from 'common/loader/SHLoader';
import Image from 'common/image/Image';
import withConfig from 'config/withConfig';
import UserUtils from 'user/user-utils';
import CreditCardSelect from 'user/card/CreditCardSelect';
import PromocodeControl from 'user/promo/PromocodeControl';
import {updateData, submitPromo} from 'checkout/checkout-actions';
import {getCreditWallet} from 'checkout/checkout-selectors';
import CheckoutUtils from 'checkout/checkout-utils';
import CheckoutPanel from 'checkout/common/CheckoutPanel';
import PurchaseButton from 'checkout/payment/PurchaseButton';
import ReviewPurchase from 'checkout/summary/ReviewPurchase';
import StripeUtils from 'utils/stripe';

class PaymentInformation extends Component {
    static propTypes = {
        ...CREDIT_WALLET_DATA,
        isMobile: PropTypes.bool.isRequired,
        isFirstView: PropTypes.bool.isRequired,
        user: PropTypes.shape(USER_DATA).isRequired,
        isMonthly: PropTypes.bool,
        hourlyRates: PropTypes.arrayOf(PropTypes.object),
        monthlyRates: PropTypes.arrayOf(PropTypes.object),
        cancellationAllowed: PropTypes.string,
        spotId: PropTypes.number,
        selectedRate: PropTypes.object,
        selectedCreditCard: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        paymentRequired: PropTypes.bool,
        paymentRequiredNoCharge: PropTypes.bool,
        isNewCreditCard: PropTypes.bool,
        purchaseOnBehalfOfCustomer: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        promoCode: PropTypes.shape(PROMO_CODE_DATA),
        incomingPromoCode: PropTypes.string,
        checkoutError: PropTypes.shape({
            title: PropTypes.string,
            subtitle: PropTypes.string,
            errors: PropTypes.arrayOf(PropTypes.string),
        }),
        reservationDetailsEdit: PropTypes.bool.isRequired,
        isPurchasing: PropTypes.bool.isRequired,
        isRebookingReservation: PropTypes.bool,
        validateReservationInfo: PropTypes.func.isRequired,
        onReserveClick: PropTypes.func.isRequired,
        onUpdateData: PropTypes.func.isRequired,
        onSubmitPromo: PropTypes.func.isRequired,
        isLoadingRates: PropTypes.bool.isRequired,
    };
    _paymentRequest = null;
    _creditExceedsPrice = false;
    _showPaymentMethods = true;
    _isPaymentRequired = true;

    constructor(props) {
        super(props);

        const {user, selectedCreditCard} = props;

        this.state = {
            isStripeLoaded: false,
            prevUser: user,
            prevSelectedCard: selectedCreditCard,
            isUserAddingNewCard: selectedCreditCard === 'new',
            creditCardSelectKey: uuidV4(),
            isPaymentRequestAvailable: false,
            isApplePayAvailable: false,
            isApplePaySelected: false,
            isGooglePaySelected: false,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {user, selectedCreditCard} = nextProps;
        const {
            prevUser,
            prevSelectedCard,
            isPaymentRequestAvailable,
            isApplePayAvailable,
        } = prevState;

        if (
            user.status === UserUtils.AUTH_STATE.LOGGED_OUT ||
            user.email !== prevUser.email ||
            selectedCreditCard !== prevSelectedCard
        ) {
            const hasNoCards =
                !user.creditCards ||
                (user.creditCards && user.creditCards.length === 0);

            return {
                prevUser: user,
                prevSelectedCard: selectedCreditCard,
                creditCardSelectKey: uuidV4(),
                isApplePaySelected: isApplePayAvailable && hasNoCards,
                isGooglePaySelected: isPaymentRequestAvailable && hasNoCards,
            };
        }

        return null;
    }

    componentDidMount() {
        this._loadStripe();
    }

    componentDidUpdate(prevProps, prevState) {
        const {onUpdateData} = this.props;
        const {isUserAddingNewCard} = this.state;
        const {paymentRequired, isNewCreditCard} = prevProps;

        if (paymentRequired !== this._isPaymentRequired) {
            onUpdateData({
                paymentRequired: this._isPaymentRequired,
            });
        } else if (isNewCreditCard !== isUserAddingNewCard) {
            // this is used to update the panel preview for payment info
            onUpdateData({
                isNewCreditCard: isUserAddingNewCard,
            });
        }
    }

    _onOpen() {
        setTimeout(() => {
            AnimationUtils.scrollTo(
                'html',
                DOMUtils.getDocumentOffset('.CheckoutPanel-reservation').top
            );
        }, 500);
    }

    _onRemovePromoClick = evt => {
        evt.preventDefault();

        this.props.onUpdateData({
            paymentRequired: true,
            paymentRequiredNoCharge: false,
            promoCode: null,
            incomingPromoCode: null,
        });
    };

    _onCancelPromoCode = () => {
        this.props.onUpdateData({
            promoCode: null,
        });
    };

    _onCardSelected = value => {
        this.setState({
            isUserAddingNewCard: value === 'new',
            isApplePaySelected:
                value === CheckoutUtils.PAYMENT_REQUEST_TYPES.APPLE_PAY,
            isGooglePaySelected:
                value === CheckoutUtils.PAYMENT_REQUEST_TYPES.GOOGLE_PAY,
        });
    };

    async _loadStripe() {
        const {
            currencyType,
            user: {creditCards},
            onReserveClick,
        } = this.props;
        const hasNoCards =
            !creditCards || (creditCards && creditCards.length === 0);
        let isPaymentRequestAvailable = false;
        let isApplePayAvailable = false;

        await StripeUtils.init();

        // create a payment request with a random price just to verify if we can show it
        this._paymentRequest = StripeUtils.createPaymentRequest({
            currencyType,
            price: 0,
            pending: true,
        });
        // check if user's browser has support for Stripe's Payment Request Button
        const result = await this._paymentRequest.canMakePayment();

        if (result) {
            isPaymentRequestAvailable = true;
            isApplePayAvailable = result.applePay;

            this._paymentRequest.on('token', paymentOverlay => {
                // paymentOverlay contains token.id and complete('success/fail')
                onReserveClick({
                    paymentOverlay,
                    stripePaymentType: isApplePayAvailable
                        ? CheckoutUtils.PAYMENT_REQUEST_TYPES.APPLE_PAY
                        : CheckoutUtils.PAYMENT_REQUEST_TYPES.GOOGLE_PAY,
                });
            });
        }

        this.setState({
            isStripeLoaded: true,
            isPaymentRequestAvailable,
            isApplePayAvailable,
            isApplePaySelected: isApplePayAvailable && hasNoCards,
            isGooglePaySelected: isPaymentRequestAvailable && hasNoCards,
        });
    }

    _renderCreditCardRequired() {
        const {
            paymentRequiredNoCharge,
            purchaseOnBehalfOfCustomer,
        } = this.props;
        const showRequiredAlert =
            (paymentRequiredNoCharge || this._creditExceedsPrice) &&
            !purchaseOnBehalfOfCustomer;

        if (!this._isPaymentRequired) {
            return null;
        }

        return (
            <div className="payment-required-content">
                {showRequiredAlert && (
                    <Alert type="highlight">
                        A credit card is required to guarantee your reservation.
                        It will not be charged.
                    </Alert>
                )}
            </div>
        );
    }

    _renderPaymentRequired() {
        const {isMonthly, user, selectedCreditCard} = this.props;
        const {
            creditCardSelectKey,
            isPaymentRequestAvailable,
            isApplePayAvailable,
        } = this.state;

        return (
            <>
                {this._renderCreditCardRequired()}
                {this._showPaymentMethods && (
                    <CreditCardSelect
                        key={creditCardSelectKey}
                        ref={node => {
                            this._creditCardSelect = node;
                        }}
                        user={user}
                        selectedCard={selectedCreditCard}
                        isMonthly={isMonthly}
                        isPaymentRequestAvailable={isPaymentRequestAvailable}
                        isApplePayAvailable={isApplePayAvailable}
                        onCardSelected={this._onCardSelected}
                    />
                )}
            </>
        );
    }

    _renderPromoApplied() {
        const {code, promoCodeDetails} = this.props.promoCode;

        return (
            <div className="PaymentInformation-promo-applied">
                Promo Code: {code}
                {promoCodeDetails && (
                    <>
                        {' ('}
                        <span className="discount-amount">
                            {promoCodeDetails}
                        </span>
                        )
                    </>
                )}
                <TextButton onClick={this._onRemovePromoClick}>
                    Remove Promo Code
                </TextButton>
            </div>
        );
    }

    _renderPromoEntry() {
        const {
            isMobile,
            spotId,
            selectedRate,
            user,
            incomingPromoCode,
            onSubmitPromo,
        } = this.props;
        let promoCode = '';
        let active = false;

        // is user is logged in and incomingPromoCode exists, submit it
        if (incomingPromoCode) {
            promoCode = incomingPromoCode;
            active = true;
        }

        return (
            <Form
                ref={node => {
                    this._promoCode = node;
                }}
                id="PaymentInformation-promo-code-form"
            >
                <PromocodeControl
                    placeholder={isMobile ? 'Promo Code' : ''}
                    spotId={spotId}
                    rate={selectedRate}
                    promoCode={promoCode}
                    active={active}
                    userEmail={user.email}
                    verifyInline
                    enterSubmits
                    onSubmitPromo={onSubmitPromo}
                    onCancelPromoCode={this._onCancelPromoCode}
                />
            </Form>
        );
    }

    _renderNotPurchaseForCustomer() {
        const {promoCode, credits, hasUserCredit} = this.props;

        return (
            <>
                {hasUserCredit && (
                    <p>{`Available SpotHero Credit: ${credits}`}</p>
                )}
                {promoCode && !isNaN(promoCode.discountAmount)
                    ? this._renderPromoApplied()
                    : this._renderPromoEntry()}
                {this._renderPaymentRequired()}
            </>
        );
    }

    submit() {
        const {
            user,
            selectedCreditCard,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            promoCode,
        } = this.props;
        let selectedCard = selectedCreditCard;
        let reservationData = false;
        let listValues;
        let promoValues;

        if (this._creditCardSelect) {
            listValues = this._creditCardSelect.submit();

            if (selectedCard !== 'new') {
                selectedCard = listValues.selectedCreditCard;
            }
        }

        if (this._promoCode) {
            promoValues = this._promoCode.submit();

            if (!promoValues) {
                return false;
            }
        }

        if (listValues && !isBoolean(listValues)) {
            if (listValues.selectedCreditCard !== 'new') {
                // if the credit card list submission passes and the selection is not a new credit card
                // we go ahead and set selectedCreditCard to the selected value as an integer
                reservationData = {
                    selectedCreditCard: parseInt(
                        listValues.selectedCreditCard,
                        10
                    ),
                };
            } else {
                // if it is a new card we pull the data from the new card form and set that accordingly
                const {fullName, saveCreditCardAsDefault} = listValues;

                // a new card is being added
                reservationData = {
                    selectedCreditCard: 'new',
                    newCardInfo: {
                        fullName,
                        isDefault: saveCreditCardAsDefault,
                    },
                };
            }
        }

        if (!reservationData && selectedCard && selectedCard !== 'new') {
            reservationData = {
                selectedCreditCard: selectedCard,
            };
        }

        if (isBoolean(promoValues) && !promoValues) {
            return false;
        } else if (isNil(promoCode) && promoValues && promoValues.promoCode) {
            // if the promoCode wasn't already applied by clicking apply it won't be defined on the reservation data so add it
            const newPromoCode = {
                code: promoValues.promoCode,
            };

            if (reservationData) {
                reservationData.promoCode = newPromoCode;
            } else {
                reservationData = {
                    promoCode: newPromoCode,
                };
            }
        }

        if (
            (this._creditExceedsPrice &&
                user.isAdmin &&
                purchaseOnBehalfOfCustomer) ||
            purchaseForCustomer
        ) {
            reservationData = {
                selectedCreditCard: 'admin',
            };
        }

        return reservationData;
    }

    render() {
        const {
            isMobile,
            user: {isAdmin, status},
            currencyType,
            credit,
            credits,
            hasUserCredit,
            isFirstView,
            isMonthly,
            hourlyRates,
            monthlyRates,
            cancellationAllowed,
            selectedRate,
            promoCode,
            paymentRequired,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer,
            reservationDetailsEdit,
            isPurchasing,
            isRebookingReservation,
            checkoutError,
            validateReservationInfo,
            onReserveClick,
            isLoadingRates,
        } = this.props;
        const {
            isStripeLoaded,
            isPaymentRequestAvailable,
            isApplePaySelected,
            isGooglePaySelected,
        } = this.state;
        const hasMultipleRates =
            (selectedRate &&
                !isMonthly &&
                hourlyRates &&
                hourlyRates.length > 1) ||
            (isMonthly && monthlyRates && monthlyRates.length > 1);
        const isOpen = Boolean(
            selectedRate &&
                status !== UserUtils.AUTH_STATE.LOGGED_OUT &&
                !reservationDetailsEdit &&
                (!isFirstView || !hasMultipleRates)
        );
        const fallbackPrice = selectedRate ? selectedRate.price : 0;
        const price = !isMonthly
            ? get(selectedRate, 'price_breakdown.total_price', fallbackPrice)
            : fallbackPrice;
        const {amountToCharge} = CheckoutUtils.getAmountToCharge({
            price,
            discount: promoCode ? promoCode.discountAmount * 100 : 0, // TODO bk - should come to us in pennies
            userCredit: credit,
        });
        const showPurchaseButtonAdminBorder =
            isAdmin &&
            !isMobile &&
            (purchaseForCustomer ||
                purchaseOnBehalfOfCustomer ||
                isRebookingReservation);
        let discountedPrice = price / 100;

        this._creditExceedsPrice = false;
        this._showPaymentMethods = true;
        this._isPaymentRequired = paymentRequired;

        if (promoCode) {
            discountedPrice -= promoCode.discountAmount;

            if (discountedPrice <= 0) {
                this._creditExceedsPrice = true;
            }
        }

        // make sure the discountedPrice is at least 0
        discountedPrice = discountedPrice > 0 ? discountedPrice : 0;

        if (
            status === UserUtils.AUTH_STATE.USER &&
            credit / 100 >= discountedPrice
        ) {
            this._creditExceedsPrice = true;
        }

        // check if the user has credit cards and if they have enough credit to pay for the whole reservation as well
        // as if the rate is not a monthly recurrable rate in which case we don't have to show the payment methods
        if (
            (this._creditExceedsPrice &&
                isAdmin &&
                purchaseOnBehalfOfCustomer) ||
            purchaseForCustomer
        ) {
            this._showPaymentMethods = false;
        }

        return (
            <CheckoutPanel
                className="CheckoutPanel-payment"
                title={
                    <>
                        <em>3</em> Payment Details
                    </>
                }
                additionalTitleMarkup={
                    <Image
                        className="digicert-seal-image"
                        cloudinaryImageId="logos/digicert-secured-seal"
                        width={73}
                        height={40}
                        alt="Norton DigiCert Seal"
                    />
                }
                open={isOpen}
                onOpen={this._onOpen}
            >
                {!isStripeLoaded ? (
                    <SHLoader />
                ) : (
                    <>
                        {selectedRate && (
                            <div className="PaymentInformation">
                                {!isLoadingRates && <ReviewPurchase />}
                                {!(
                                    purchaseForCustomer ||
                                    isRebookingReservation
                                ) && this._renderNotPurchaseForCustomer()}
                                {(purchaseForCustomer ||
                                    isRebookingReservation) && (
                                    <>
                                        <div className="well well-sm">
                                            <p>
                                                Purchasing for customer, no
                                                payment required.
                                            </p>
                                        </div>
                                        {hasUserCredit && (
                                            <p>{`Available SpotHero Credit: ${credits}`}</p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        <PurchaseButton
                            isMonthly={isMonthly}
                            recurrable={selectedRate?.recurrable}
                            paymentRequest={this._paymentRequest}
                            currencyType={currencyType}
                            price={amountToCharge}
                            promoCode={promoCode}
                            priceBreakdownItems={get(
                                selectedRate,
                                'price_breakdown.items'
                            )}
                            showPrice={false}
                            loading={isPurchasing}
                            disabled={
                                isPurchasing || !selectedRate || isLoadingRates
                            }
                            isPaymentRequestAvailable={
                                isPaymentRequestAvailable
                            }
                            isApplePaySelected={isApplePaySelected}
                            isGooglePaySelected={isGooglePaySelected}
                            validateReservationInfo={validateReservationInfo}
                            onReserveClick={onReserveClick}
                            showAdminBorder={showPurchaseButtonAdminBorder}
                            cancellationAllowed={cancellationAllowed}
                        />
                        {checkoutError && (
                            <FormGroupError>
                                {checkoutError.subtitle && (
                                    <h1 className="heading-sm">
                                        {checkoutError.subtitle}
                                    </h1>
                                )}
                                {checkoutError.errors[0]}
                            </FormGroupError>
                        )}
                    </>
                )}
            </CheckoutPanel>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            selectedCreditCard,
            paymentRequired,
            paymentRequiredNoCharge,
            isNewCreditCard,
            purchaseOnBehalfOfCustomer,
            purchaseForCustomer,
            promoCode,
            incomingPromoCode,
            reservationDetailsEdit,
            isPurchasing,
            isRebookingReservation,
            checkoutError,
        },
        searchRequest: {monthly: isMonthly},
        spot: {
            selected: {
                spotId,
                selectedRate,
                hourly_rates: hourlyRates,
                monthly_rates: monthlyRates,
                facility: {cancellation_allowed: cancellationAllowed},
            },
        },
        user: {data: user},
    } = state;
    const wallet = getCreditWallet(state);
    const hasUserCredit =
        Boolean(wallet.credits) &&
        (!promoCode || (paymentRequired && !paymentRequiredNoCharge));

    return {
        ...wallet,
        user,
        isMonthly,
        hasUserCredit,
        hourlyRates,
        monthlyRates,
        cancellationAllowed,
        spotId,
        selectedRate,
        selectedCreditCard,
        paymentRequired,
        paymentRequiredNoCharge,
        isNewCreditCard,
        purchaseOnBehalfOfCustomer,
        purchaseForCustomer,
        promoCode,
        incomingPromoCode,
        reservationDetailsEdit,
        isPurchasing,
        isRebookingReservation,
        checkoutError,
    };
};

const mapDispatchToProps = {
    onUpdateData: updateData,
    onSubmitPromo: submitPromo,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})
);

export default enhance(PaymentInformation);
